import { uniqBy } from "lodash";
import * as VisitorServiceMapper from "../../../../api/mappers/VisitorServiceMapper";
import apiService from "../../../../api/rest";
import favorites from "./favorites";
import {
  OAPI_API_TYPE,
  SENTRY_ERRORS,
  SAVE_CARD_WALLET_ERROR,
  SAVE_CARD_WALLET_SUCCESS,
  DELETE_CARD_WALLET_ERROR,
  DELETE_CARD_WALLET_SUCCESS,
  USER_LOGIN,
  GUEST_LOGIN,
  NOT_IDENTIFIED
} from "../../../../app-constants";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import router from "@/routes";
import { v4 as uuidv4 } from "uuid";

export const getDefaultState = () => {
  return {
    visitorId: "",
    userData: null,
    userOrders: null,
    guestUserData: null,
    mobileUserData: null,
    creditCards: [],
    previousOrders: [],
    genericUserId: uuidv4()
  };
};

// initial state
const state = getDefaultState();

const getters = {
  isLoggedIn(state) {
    return state.userData && state.userData.id ? state.userData.id !== "" && state.userData.id !== null : false;
  },
  getUserData(state) {
    return state.userData;
  },
  getGuestUserData(state) {
    return state.guestUserData;
  },
  getVisitorId(state) {
    return state.visitorId;
  },
  getMobileUserData(state) {
    return state.mobileUserData;
  },
  getUserOrders(state) {
    return state.userOrders;
  },
  getUserType(state) {
    return state.userData ? state.userData.type : null;
  },
  isGuestUser(state) {
    return !!state.guestUserData?.email && !state.userData;
  },
  getUserPhoneNumber(state) {
    return state.userData ? state.userData.phone || "" : "";
  },
  getPreviousOrders(state) {
    return state.previousOrders;
  },
  getVisitorCreditCards(state) {
    return state.creditCards;
  },
  isUserIdentified(state) {
    if (state.userData) {
      const {
        id,
        profile: { email }
      } = state.userData;
      return !!(id && email);
    }

    if (state.guestUserData) {
      const { name, phone } = state.guestUserData || {};
      return !!(name && phone);
    }

    return false;
  },
  getGenericUserId(state) {
    return state.genericUserId;
  },
  getUserLoginStatus(state) {
    if (state.userData) return USER_LOGIN;
    if (state.guestUserData) return GUEST_LOGIN;
    return NOT_IDENTIFIED;
  }
};

const actions = {
  async authorization({ commit, dispatch, rootGetters }, { email, password, remember }) {
    try {
      const domainId = rootGetters["common/getDomainId"];
      const loginResponse = await apiService.loginUser({ email, password, domainId });
      const {
        visitorUserData: { visitor },
        mobileUserData,
        iwToken,
        iwRefreshToken
      } = loginResponse.data;
      const { id } = visitor;
      commit("setVisitorId", id);
      commit("common/setIwToken", iwToken, { root: true });
      commit("common/setRefreshToken", iwRefreshToken, { root: true });
      commit("setMobileUserInfo", { ...mobileUserData, phone: visitor.profile.phone });
      return dispatch("setUserData", visitor);
    } catch (reject) {
      return Promise.reject(reject.response);
    }
  },
  async loginGuestUser({ commit }, phone = "") {
    try {
      const {
        data: { user, iwToken, iwRefreshToken }
      } = await apiService.authGuest();
      commit("common/setIwToken", iwToken, { root: true });
      commit("common/setRefreshToken", iwRefreshToken, { root: true });
      commit("setMobileUserInfo", { ...user, phone });
      return iwToken;
    } catch (reject) {
      return Promise.reject(reject.response);
    }
  },
  async registration({ dispatch, rootGetters, commit }, userData) {
    const source = rootGetters["common/getVisitorSource"];
    const STATUS = "VISITOR_STATUS_ACTIVE";

    try {
      const {
        data: { iwToken, iwRefreshToken, mobileUserData, visitorUserData }
      } = await apiService.authRegister({ ...userData, status: STATUS, source });

      commit("setVisitorId", visitorUserData.visitor.id);
      commit("common/setIwToken", iwToken, { root: true });
      commit("common/setRefreshToken", iwRefreshToken, { root: true });
      commit("setMobileUserInfo", { ...mobileUserData, phone: visitorUserData.visitor.profile.phone });
      dispatch("setUserData", visitorUserData.visitor);
    } catch (reject) {
      return Promise.reject(reject.response?.status);
    }
  },
  createGuestUser({ commit, dispatch }, newUser) {
    commit("setGuestUserInfo", newUser);
    return dispatch("loginGuestUser", newUser.phone);
  },
  checkUserIsAuthorized({ getters }) {
    return new Promise((resolve, reject) => {
      if (getters.isUserIdentified) {
        return resolve(true);
      }
      return reject(false);
    });
  },
  updateUserInfo({ commit, getters, rootGetters }, user) {
    const userData = getters["getUserData"];
    const domainId = rootGetters["common/getDomainId"];
    const { id } = userData;
    const reqBody = {
      profile: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone.replace(/[^0-9]/g, ""),
        birthday: user.birthday,
        avatar_image: user.avatar_image,
        address: {
          address_line_1: user.address.address_line_1,
          address_line_2: user.address.address_line_2,
          city: user.address.city,
          zip: user.address.zip
        }
      }
    };
    if (user.newPassword) {
      reqBody["password"] = user.newPassword;
    }

    return apiService.updateUser(id, domainId, reqBody).then(({ data }) => {
      commit("updateProfileData", reqBody.profile);
      return Promise.resolve(data);
    });
  },
  /**
   * Sets IW user into an initial state
   *
   */
  async logOutUser({ commit, dispatch, getters, rootGetters }) {
    if (!getters.getUserData) return;
    commit("logOut");
    commit("resetMobileUserInfo");
    dispatch("cart/clearShoppingCart", null, { root: true });
    dispatch("cleanFavorites");

    try {
      //renegotiate iwToken
      const venueId = rootGetters["venue/getActiveVenue"];
      await dispatch("common/authenticate", venueId, { root: true });
    } catch (e) {
      router.push("/not-found");
    }
  },
  fetchOrders({ commit, state, getters }) {
    if (!getters.getUserOrders) {
      return apiService.fetchUserOrders(state.userData.id).then(
        (response) => {
          commit("setUserOrders", response.data.orders);
          return Promise.resolve();
        },
        () => {
          return Promise.reject();
        }
      );
    } else {
      return Promise.resolve(getters.getUserOrders);
    }
  },
  setUserData({ commit }, payload) {
    commit("setUserInfo", payload);
    return payload;
  },
  async fetchVisitorOrders({ commit, rootGetters }) {
    const visitorId = rootGetters["user/getVisitorId"];
    if (!visitorId) return;

    // filters items against the current active vendor, it will return a menu item rather then a cart item.
    const getOrderItemsByVendor = (items) => {
      const resultItems = [];
      items.forEach((item) => {
        if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE && item.rowId > 10000) return;
        const newItem = rootGetters["vendor/getItemByID"](`${item.id}`);
        if (newItem) resultItems.push(Object.assign({}, newItem, { quantity: item.quantity || 1 }));
      });

      return resultItems;
    };

    try {
      const response = await apiService.fetchVisitorOrders(visitorId);
      const orders = response.data.orders || [];
      const resultOrders = [];
      let items = [];

      // grabs current menu items from active vendor.
      orders.forEach((order) => {
        items = getOrderItemsByVendor(order.cart.items);
        if (items && items.length) {
          resultOrders.push({
            orderDate: order.orderDate,
            orderDateTz: order.orderDateTz,
            orderId: order.orderId,
            items
          });
        }
      });

      commit("setVisitorPreviousOrders", resultOrders);
      return Promise.resolve();
    } catch (reject) {
      return Promise.reject(reject.response);
    }
  },
  resetPassword({ rootGetters }, email) {
    const domainId = rootGetters["common/getDomainId"];
    const venueId = rootGetters["venue/getActiveVenue"].toString();
    const redirectUri = `${location.origin}/${location.pathname.split("/")[1]}/login`;

    if (!venueId || !domainId) return Promise.reject();
    return apiService.resetPassword(domainId, email, venueId, redirectUri).then(
      (response) => {
        return Promise.resolve(response);
      },
      (reject) => {
        return Promise.reject(reject);
      }
    );
  },
  async checkForgotPasswordHash(args, data) {
    const { domainID, visitorID, key } = data;
    if (!domainID || !visitorID || !key) return Promise.reject();
    try {
      await apiService.checkForgotPasswordHash(domainID, visitorID, key);
    } catch (e) {
      return Promise.reject(e.response);
    }
  },
  async patchUserPassword(args, { domainID, visitorID, password }) {
    // TODO: add check for visitorID
    if (!password && !domainID) return Promise.reject();
    try {
      await apiService.patchUserPassword(domainID, visitorID, password);
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e.response);
    }
  },
  async saveCreditCardToken({ rootGetters, dispatch }, cardTokenInfo) {
    let responseData = null;
    let success = false;
    let msgObj = {
      message: SAVE_CARD_WALLET_SUCCESS,
      type: "success"
    };

    try {
      const domainId = rootGetters["common/getDomainId"];
      const visitorId = rootGetters["user/getVisitorId"];
      const request = VisitorServiceMapper.mapPostSingleCreditCardTokenRequest(cardTokenInfo);
      const response = await apiService.saveCreditCardTokens(request, domainId, visitorId);
      if (response.status === 200) {
        success = true;
      } else {
        msgObj = {
          message: SAVE_CARD_WALLET_ERROR,
          type: "error"
        };
        success = false;
        responseData = response.data.message || "Failed saving card data.";
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_OAPI_VS_SAVETOKEN,
          "checkoutError",
          "saveCreditCardToken",
          {},
          response
        );
      }
    } catch (error) {
      msgObj = {
        message: SAVE_CARD_WALLET_ERROR,
        type: "error"
      };
      responseData = error || "Failed saving card data.";
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_VS_SAVETOKEN, "checkoutError", "saveCreditCardToken", {}, error);
    }
    dispatch("messages/addMessage", msgObj, { root: true });
    return Promise.resolve({
      success: success,
      data: responseData
    });
  },
  async deleteCreditCardToken({ rootGetters, dispatch }, tokenInfo) {
    let responseData = null;
    let success = false;
    let msgObj = {
      message: DELETE_CARD_WALLET_SUCCESS,
      type: "success"
    };

    try {
      const domainId = rootGetters["common/getDomainId"];
      const visitorId = rootGetters["user/getVisitorId"];
      const response = await apiService.deleteCreditCardToken(tokenInfo.gateway_id, tokenInfo.token, domainId, visitorId);

      if (response.status === 200) {
        success = true;
      } else {
        msgObj = {
          message: DELETE_CARD_WALLET_ERROR,
          type: "error"
        };
        success = false;
        responseData = response.data.message || "Failed deleting card data.";
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_OAPI_VS_DELETETOKEN,
          "walletError",
          "deleteCreditCardToken",
          {},
          response
        );
      }
    } catch (error) {
      msgObj = {
        message: DELETE_CARD_WALLET_ERROR,
        type: "error"
      };
      responseData = error || "Failed deleting card data.";
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_VS_DELETETOKEN, "walletError", "deleteCreditCardToken", {}, error);
    }

    dispatch("messages/addMessage", msgObj, { root: true });

    return Promise.resolve({
      success: success,
      data: responseData
    });
  },
  async fetchVisitorCreditCards({ commit, rootGetters }) {
    const visitorId = rootGetters["user/getVisitorId"];
    const userData = rootGetters["user/getUserData"];
    const domainId = rootGetters["common/getDomainId"];
    if (!visitorId || !domainId || !userData) return;

    const response = await apiService.fetchUserCreditCards(domainId, visitorId);
    commit("setVisitorsCreditCards", uniqBy(response.data, "token"));
  }
};

const mutations = {
  setUserInfo(state, user) {
    state.userData = user;
  },
  updateProfileData(state, data) {
    state.userData.profile = data;
  },
  setMobileUserInfo(state, user) {
    state.mobileUserData = user;
  },
  resetMobileUserInfo(state) {
    state.mobileUserData = null;
  },
  setGuestUserInfo(state, guest) {
    state.guestUserData = guest;
  },
  setUserPhoneNumber(state, phone) {
    state.userData = { ...state.userData, phone };
  },
  setUserOrders(state, orders) {
    state.userOrders = orders;
  },
  setVisitorId(state, visitorId) {
    state.visitorId = visitorId;
  },
  setVisitorPreviousOrders(state, previousOrders) {
    state.previousOrders = previousOrders;
  },
  logOut(state) {
    state.visitorId = "";
    state.userData = null;
    state.creditCards = [];
  },
  resetState(state) {
    const defaultState = getDefaultState();
    //To conserve the generic userId, this is used to Analytics purposes.
    Object.assign(defaultState, { genericUserId: state.genericUserId });
    Object.assign(state, defaultState);
  },
  setVisitorsCreditCards(state, creditCards) {
    state.creditCards = creditCards;
  }
};

export default {
  namespaced: true,
  modules: {
    favorites
  },
  state,
  getters,
  actions,
  mutations
};
